import React, { FunctionComponent, SVGProps, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { TableButton } from "Components/UI/DataTable";
import Icon from "Components/UI/Icon/Icon";
import {
  FilterOption,
  FilterOptionList,
  FilterPanelFooter,
  FilterPopover,
  FilterTrigger,
} from "Components/UI/TableFilters/TableFilters";
import { changeToParams } from "Shared/Utils/search-params";

type ModalFilterProps = {
  option: {
    label: string;
    value: string;
    img?: FunctionComponent<SVGProps<SVGSVGElement>>;
  }[];
  filterName: string;
  title?: string;
  isClearable?: boolean;
  onOptionChange?: (searchParams: URLSearchParams, value: string) => void;
};

/** Single-select chip over a fixed list, bound to the `filterName` URL param. */
const ModalFiltersStaticOptions: React.FC<ModalFilterProps> = ({
  option,
  filterName,
  isClearable = true,
  title,
  onOptionChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState("");
  const applied = searchParams.get(filterName) || "";
  const appliedLabel = option.find((el) => el.value === applied)?.label ?? applied;
  const label = title || "Filter";

  const commit = (value: string) => {
    onOptionChange?.(searchParams, value);
    searchParams.delete("page");
    setSearchParams(changeToParams(filterName, value, searchParams));
    setAnchor(null);
  };

  return (
    <>
      <FilterTrigger
        label={label}
        value={appliedLabel}
        active={!!applied}
        open={!!anchor}
        onClick={(event) => {
          setDraft(applied);
          setAnchor(event.currentTarget);
        }}
        onClear={isClearable ? () => commit("") : undefined}
      />
      <FilterPopover
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        label={`Filter ${label}`}
      >
        <FilterOptionList>
          {option.map((el) => (
            <FilterOption
              key={el.value}
              selected={draft === el.value}
              icon={el.img ? <Icon component={el.img} /> : undefined}
              onClick={() => setDraft(el.value)}
            >
              {el.label}
            </FilterOption>
          ))}
        </FilterOptionList>
        <FilterPanelFooter
          start={
            isClearable ? (
              <TableButton onClick={() => commit("")} disabled={!applied && !draft}>
                Clear
              </TableButton>
            ) : undefined
          }
        >
          <TableButton variant="outline" onClick={() => setAnchor(null)}>
            Cancel
          </TableButton>
          <TableButton variant="primary" onClick={() => commit(draft)}>
            Apply
          </TableButton>
        </FilterPanelFooter>
      </FilterPopover>
    </>
  );
};

export default ModalFiltersStaticOptions;
